import type {
  FulfillmentAcceptance,
  FulfillmentErrorCode,
  FulfillmentErrorKey,
} from './store-fulfillment';
import type { UUID } from './common';
import type { AdminCourierProof, CourierOrderAssignmentSummary } from './courier';

/** Order status state machine values. */
export type OrderStatus = 'ordered' | 'shipped' | 'delivered' | 'cancelled' | 'rejected';

export const ORDER_STATUSES: readonly OrderStatus[] = [
  'ordered',
  'shipped',
  'delivered',
  'cancelled',
  'rejected',
] as const;

export const CUSTOMER_ACTIVE_ORDER_STATUSES: readonly OrderStatus[] = ['ordered', 'shipped'];

export const ADMIN_INCOMING_ORDER_STATUSES: readonly OrderStatus[] = ['ordered'];

export const TERMINAL_ORDER_STATUSES: readonly OrderStatus[] = [
  'delivered',
  'cancelled',
  'rejected',
] as const;

export type ManualApprovalStatus = 'not_required' | 'pending' | 'approved' | 'declined';

export const MANUAL_APPROVAL_STATUSES: readonly ManualApprovalStatus[] = [
  'not_required',
  'pending',
  'approved',
  'declined',
] as const;

export type AdminOrderReviewFilter = 'awaiting_approval';

export interface ManualApprovalFields {
  readonly manual_approval_status: ManualApprovalStatus;
  readonly manual_approval_required_at: string | null;
  readonly manual_approved_at: string | null;
  readonly manual_approved_by_user_id: UUID | null;
  readonly manual_declined_at: string | null;
  readonly manual_declined_by_user_id: UUID | null;
}

/**
 * Row shape for `public.orders`. Consumed by F-017 (create), F-018
 * (confirmation), F-019 (history), F-020 (polling), F-022 (admin detail).
 *
 * Iter 2 W2: subtotal_cents is integer cents (agorot), matching the cart
 * convention. formatPrice() can be called directly without conversion.
 */
export interface Order {
  readonly id: UUID;
  readonly user_id: UUID | null;
  readonly store_id: UUID;
  readonly status: OrderStatus;
  readonly delivery_method: DeliveryMethod;
  readonly delivery_pricing_state: 'legacy_unconfigured' | 'priced';
  readonly delivery_fee_cents: number | null;
  readonly total_cents: number | null;
  readonly fulfillment_version: 1 | null;
  readonly delivery_rate_revision: number | null;
  readonly pickup_place_id_snapshot: string | null;
  readonly pickup_place_name_snapshot: string | null;
  readonly pickup_place_details_snapshot: string | null;
  readonly delivery_locality_id: UUID | null;
  readonly delivery_locality_name_ar_snapshot: string | null;
  readonly delivery_locality_name_he_snapshot: string | null;
  readonly delivery_locality_name_en_snapshot: string | null;
  readonly delivery_locality_symbol_number_snapshot: number | null;
  readonly customer_name_snapshot: string;
  readonly customer_phone_snapshot: string | null;
  readonly address_label_snapshot: string | null;
  readonly address_recipient_snapshot: string | null;
  readonly address_line_1_snapshot: string | null;
  readonly address_line_2_snapshot: string | null;
  readonly address_city_snapshot: string | null;
  readonly address_region_snapshot: string | null;
  readonly address_postal_code_snapshot: string | null;
  readonly address_country_code_snapshot: string | null;
  readonly address_phone_snapshot: string | null;
  readonly delivery_latitude: number | null;
  readonly delivery_longitude: number | null;
  /** Subtotal in integer cents (agorot). Use formatPrice() directly. */
  readonly subtotal_cents: number;
  readonly currency: string;
  readonly customer_notes: string | null;
  readonly shipped_at: string | null;
  readonly delivered_at: string | null;
  readonly cancelled_at: string | null;
  readonly rejected_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface AdminOrder extends Order, ManualApprovalFields {}

export interface AdminShippingLabelOrder extends AdminOrder {
  readonly courier_assignment?: CourierOrderAssignmentSummary | null;
}

export type DeliveryMethod = 'address' | 'location' | 'pickup';

/**
 * Row shape for `public.order_items`.
 *
 * Iter 2 W1: product_image_path_snapshot (was product_image_url_snapshot).
 * Iter 2 W2: unit_price_snapshot_cents + line_total_cents are integer cents.
 */
export interface OrderItem {
  readonly id: UUID;
  readonly order_id: UUID;
  readonly product_id: UUID;
  readonly variant_id: UUID;
  readonly variant_option_summary_snapshot?: string;
  readonly product_name_snapshot: string;
  /** Storage path (not URL). Resolve via buildImageProxyUrl at render time. */
  readonly product_image_path_snapshot: string | null;
  readonly quantity: number;
  /** Unit price in integer cents (agorot). Use formatPrice() directly. */
  readonly unit_price_snapshot_cents: number;
  /** Line total in integer cents. quantity * unit_price_snapshot_cents. */
  readonly line_total_cents: number;
  readonly created_at: string;
}

/** Error codes the `create_order_from_cart` RPC can return as DATA. */
export type CreateOrderErrorCode =
  | FulfillmentErrorCode
  | 'PRODUCT_UNAVAILABLE'
  | 'VARIANT_UNAVAILABLE'
  | 'CART_EMPTY'
  | 'STORE_UNAVAILABLE';

/** Parsed result from `create_order_from_cart` RPC. */
export interface CreateOrderResult {
  readonly success: boolean;
  readonly order_id: UUID | null;
  readonly error_code: CreateOrderErrorCode | null;
  readonly unavailable_product_ids: UUID[];
  readonly unavailable_product_names: string[];
  readonly unavailable_variant_ids?: UUID[];
  readonly unavailable_line_messages?: string[];
}

interface CreateOrderBaseInput {
  readonly storeId: string;
  readonly acceptance: FulfillmentAcceptance;
  /** Optional customer notes (max 500 chars). */
  readonly customerNotes: string | null;
}

export interface CreateAddressOrderInput extends CreateOrderBaseInput {
  readonly fulfillment: { readonly method: 'delivery'; readonly pickupPlaceId: null };
  readonly deliveryLocalityId: string;
  readonly deliveryMethod?: 'address';
  readonly addressLocalityId: string;
  /** Unified delivery information supplied by checkout or a legacy full address. */
  readonly addressRecipient: string;
  readonly addressLine1: string | null;
  readonly addressLine2: string | null;
  readonly addressCity: string;
  readonly addressRegion: string | null;
  readonly addressPostalCode: string;
  readonly addressCountryCode: string;
  readonly addressPhone: string | null;
  readonly addressLabel: string | null;
  /** Optional precise delivery location for unified delivery-information orders. */
  readonly deliveryLatitude?: number | null;
  readonly deliveryLongitude?: number | null;
}

/** Input passed from the checkout page to the service. */
export interface CreatePickupOrderInput extends CreateOrderBaseInput {
  readonly fulfillment: { readonly method: 'pickup'; readonly pickupPlaceId: string };
}
export type CreateOrderInput = CreateAddressOrderInput | CreatePickupOrderInput;
type WithoutStore<T> = T extends unknown ? Omit<T, 'storeId'> : never;
export type CreateOrderMutationInput = WithoutStore<CreateOrderInput>;

export interface AdminPreparedOrderLineInput {
  readonly variant_id: UUID;
  readonly quantity: number;
}

interface CreateAdminPreparedOrderBaseInput {
  readonly acceptance: FulfillmentAcceptance;
  readonly storeId: UUID;
  readonly customerName: string;
  readonly customerPhone: string;
  readonly customerNotes: string | null;
  readonly lines: readonly AdminPreparedOrderLineInput[];
}

export type CreateAdminPreparedOrderInput = CreateAdminPreparedOrderBaseInput &
  (
    | {
        readonly fulfillment: { readonly method: 'delivery'; readonly pickupPlaceId: null };
        readonly deliveryLocalityId: UUID;
        readonly addressLine1: string;
      }
    | { readonly fulfillment: { readonly method: 'pickup'; readonly pickupPlaceId: string } }
  );

export type AdminPreparedOrderErrorCode =
  | FulfillmentErrorCode
  | 'NO_LINES'
  | 'QUANTITY_INVALID'
  | 'INSUFFICIENT_STOCK'
  | 'VARIANT_UNAVAILABLE'
  | 'PRODUCT_UNAVAILABLE'
  | 'LOCALITY_INVALID'
  | 'PHONE_INVALID'
  | 'ADDRESS_REQUIRED'
  | 'ADDRESS_TOO_LONG'
  | 'CUSTOMER_NAME_REQUIRED'
  | 'UNAUTHORIZED'
  | 'STORE_UNAVAILABLE';

export interface CreateAdminPreparedOrderResult {
  readonly success: boolean;
  readonly order_id: UUID | null;
  readonly error_code: AdminPreparedOrderErrorCode | null;
  readonly unavailable_variant_ids: UUID[];
  readonly unavailable_line_messages: string[];
}

export type AdminOrderSellableVariantUnavailableReason =
  | 'out_of_stock'
  | 'deleted_product'
  | 'inactive_variant'
  | 'deleted_variant'
  | null;

export interface AdminOrderSellableVariant {
  readonly product_id: UUID;
  readonly variant_id: UUID;
  readonly product_name: string;
  readonly product_published: boolean;
  readonly product_deleted: boolean;
  readonly variant_active: boolean;
  readonly variant_deleted: boolean;
  readonly option_summary: string;
  readonly sku: string | null;
  readonly barcode: string | null;
  readonly barcode_normalized: string | null;
  readonly thumbnail_path: string | null;
  readonly price_cents: number;
  readonly sale_price_cents: number | null;
  readonly effective_price_cents: number;
  readonly currency: 'ILS';
  readonly available_stock: number;
  readonly is_selectable: boolean;
  readonly unavailable_reason: AdminOrderSellableVariantUnavailableReason;
}

export interface SearchAdminOrderSellableVariantsInput {
  readonly storeId: UUID;
  readonly query: string;
  readonly categoryId?: UUID | null;
  readonly limit?: number;
  readonly offset?: number;
}

export type AdminPreparedOrderErrorKey =
  | FulfillmentErrorKey
  | 'admin.orders.prepare.errors.noLines'
  | 'admin.orders.prepare.errors.quantityInvalid'
  | 'admin.orders.prepare.errors.insufficientStock'
  | 'admin.orders.prepare.errors.deletedProduct'
  | 'admin.orders.prepare.errors.inactiveVariant'
  | 'admin.orders.prepare.errors.variantUnavailable'
  | 'admin.orders.prepare.errors.customerNameRequired'
  | 'admin.orders.prepare.errors.phoneRequired'
  | 'admin.orders.prepare.errors.phoneInvalid'
  | 'admin.orders.prepare.errors.localityRequired'
  | 'admin.orders.prepare.errors.localityInvalid'
  | 'admin.orders.prepare.errors.addressRequired'
  | 'admin.orders.prepare.errors.addressTooLong'
  | 'admin.orders.prepare.errors.unauthorized'
  | 'admin.orders.prepare.errors.networkFailed'
  | 'admin.orders.prepare.errors.unknown';

/** Whitelist of i18n keys that `mapOrderError` is allowed to return. */
export type OrderErrorKey =
  | 'admin.orders.fulfillment.deliveryOnly'
  | 'admin.orders.actions.collectFailed'
  | FulfillmentErrorKey
  | 'order.errors.productUnavailable'
  | 'order.errors.cartEmpty'
  | 'order.errors.storeUnavailable'
  | 'order.errors.networkFailed'
  | 'order.errors.unknown'
  // F-018: cancel-specific error keys
  | 'order.errors.cancelFailed'
  | 'order.errors.cancelAlreadyProcessed'
  | 'order.errors.orderNotFound'
  // F-023: merchant transition error keys
  | 'order.errors.invalidTransition'
  | 'order.errors.transitionUnauthorized'
  | 'order.errors.shipFailed'
  | 'order.errors.deliverFailed'
  | 'order.errors.courierProofRequired'
  | 'order.errors.manualApprovalRequired'
  | 'order.errors.rejectFailed';

/**
 * F-019 — narrow shape for a single row in the customer order history list.
 * Distinct from `Order` (which carries full address snapshots + transitions).
 * The joined `store` field mirrors the Supabase FK join response shape.
 * `store` is nullable to handle soft-deleted stores (the FK still resolves
 * to null after the store row is soft-deleted or physically removed).
 * `item_count` is derived client-side by counting `order_items(id)` length.
 */
export interface OrderHistoryRow {
  readonly delivery_method: DeliveryMethod;
  readonly delivery_pricing_state: 'legacy_unconfigured' | 'priced';
  readonly delivery_fee_cents: number | null;
  readonly total_cents: number | null;
  readonly fulfillment_version: 1 | null;

  readonly id: UUID;
  readonly status: OrderStatus;
  readonly created_at: string;
  readonly subtotal_cents: number;
  readonly currency: string;
  readonly store_id: UUID;
  readonly store: {
    readonly name: string;
    readonly logo_url: string | null;
    readonly logo_storage_path: string | null;
  } | null;
  readonly item_count: number;
}

/** Pair of counts returned by `countOrdersSummary`. */
export interface OrderCountSummary {
  readonly total: number;
  readonly active: number;
}

/**
 * F-021 — narrow shape for a single row in the merchant order list.
 * Distinct from `OrderHistoryRow` (customer-facing, shows store info).
 * `item_count` is derived client-side by counting `order_items(id)` length.
 */
export interface StoreOrderRow extends ManualApprovalFields {
  readonly delivery_method: DeliveryMethod;
  readonly delivery_pricing_state: 'legacy_unconfigured' | 'priced';
  readonly delivery_fee_cents: number | null;
  readonly total_cents: number | null;
  readonly fulfillment_version: 1 | null;

  readonly id: UUID;
  readonly status: OrderStatus;
  readonly created_at: string;
  readonly subtotal_cents: number;
  readonly currency: string;
  readonly customer_name_snapshot: string;
  readonly customer_phone_snapshot: string | null;
  readonly item_count: number;
  readonly courier_assignment?: CourierOrderAssignmentSummary | null;
}

/** Server-side sort keys for the merchant order list. */
export type StoreOrderSortBy =
  | 'created_at_desc'
  | 'created_at_asc'
  | 'total_desc'
  | 'total_asc'
  | 'subtotal_desc'
  | 'subtotal_asc';

/** Options passed to `listStoreOrders`. */
export interface ListStoreOrdersOptions {
  readonly limit: number;
  readonly offset: number;
  readonly status: OrderStatus | null; // null = all statuses
  readonly reviewFilter?: AdminOrderReviewFilter | null;
  readonly storeId: string;
  readonly sortBy: StoreOrderSortBy;
  readonly search: string | null;
}

/** Page result from `listStoreOrders`. */
export interface ListStoreOrdersPage {
  readonly rows: StoreOrderRow[];
  readonly hasMore: boolean;
}

// ── F-023 additions ─────────────────────────────────────────────────

/**
 * Structured error codes returned by the F-023 transition RPCs.
 * All four RPCs return one of these codes (or null on success).
 */
export type TransitionErrorCode =
  | FulfillmentErrorCode
  | 'INVALID_TRANSITION'
  | 'UNAUTHORIZED'
  | 'NOT_FOUND'
  | 'SHIP_FAILED'
  | 'COURIER_PROOF_REQUIRED'
  | 'MANUAL_APPROVAL_REQUIRED';

/**
 * Base result shape returned by deliver_order and the base of ship_order /
 * reject_order results.
 */
export interface TransitionResult {
  readonly success: boolean;
  readonly error_code: TransitionErrorCode | null;
  readonly order: AdminOrder | null;
}

/**
 * Result from ship_order — includes per-product inventory snapshots taken
 * while product rows are locked. Shipping does not mutate inventory; order
 * submit already decremented it.
 */
export interface InventoryQuantitySnapshot {
  readonly product_id: string;
  readonly variant_id: string;
  readonly quantity: number;
}

export interface VariantInventorySnapshot {
  readonly product_id: string;
  readonly variant_id: string;
  readonly inventory: number;
}

export interface ShipTransitionResult extends TransitionResult {
  readonly inventory_snapshots: readonly VariantInventorySnapshot[];
}

export interface RejectTransitionResult extends TransitionResult {
  readonly inventory_restored: readonly InventoryQuantitySnapshot[];
}

export type ManualApprovalErrorCode =
  | 'INVALID_TRANSITION'
  | 'UNAUTHORIZED'
  | 'NOT_FOUND'
  | 'APPROVAL_NOT_REQUIRED'
  | 'DECLINE_FAILED'
  | 'NOTIFICATION_ENQUEUE_FAILED';

export interface ManualApprovalTransitionResult {
  readonly success: boolean;
  readonly error_code: ManualApprovalErrorCode | null;
  readonly order: AdminOrder | null;
}

export interface DeclineManualOrderResult extends ManualApprovalTransitionResult {
  readonly inventory_restored: readonly InventoryQuantitySnapshot[];
  readonly notification_intent_id: UUID | null;
  readonly notification_status: 'pending' | 'skipped' | 'already_exists' | null;
}

/**
 * A single row from the order_events audit table.
 * Written by the transition RPCs on every successful state change.
 * Consumed by F-020 status timeline display.
 */
export interface OrderEvent {
  readonly id: string;
  readonly order_id: string;
  readonly from_status: OrderStatus;
  readonly to_status: OrderStatus;
  readonly actor_user_id: string;
  readonly metadata: Record<string, unknown>;
  readonly created_at: string;
}

/**
 * F-022: Full order detail shape for admin consumption.
 * Returned by getAdminOrderDetail. Never returned by getOrder
 * (which is storefront-facing — see Decision 3 in 01-requirements.md).
 */
export interface AdminOrderDetail {
  readonly order: AdminOrder;
  readonly items: OrderItem[];
  readonly events: OrderEvent[];
  readonly courier_assignment?: CourierOrderAssignmentSummary | null;
  readonly courier_proof?: AdminCourierProof | null;
}
